'use client'

import { useEffect, useRef, useState } from 'react'

import { Cell, Label, Pie, PieChart as PieChartRecharts } from 'recharts'

import { cn } from '@/lib/cn'

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../chart'

type PieChartProps = {
  id?: string
  config: ChartConfig
  data: {
    key: string
    label: string
    value: number
  }[]
  label?: string
  tooltip?: boolean
  className?: string
  multiplierOuterRadius?: number
}

const PieChart = ({
  id,
  config,
  data,
  label,
  tooltip = true,
  className,
  multiplierOuterRadius = 1.5,
}: PieChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect()
        setContainerSize({ width, height })
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const minDimension = Math.min(containerSize.width, containerSize.height)
  const innerRadius = minDimension / 4
  const fontSize = Math.max(24, Math.min(48, minDimension / 8))

  return (
    <ChartContainer
      id={id}
      config={config}
      className={cn('h-auto min-h-24 w-full', className)}
      ref={containerRef}
    >
      <PieChartRecharts>
        {tooltip && (
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
        )}
        <Pie
          data={data}
          dataKey="value"
          nameKey="label"
          innerRadius={innerRadius}
          outerRadius={innerRadius * multiplierOuterRadius}
          strokeWidth={10}
        >
          {data.map((entry) => {
            const colorConfig = config[entry.key]

            return (
              <Cell
                key={`cell-${entry.key}`}
                className={colorConfig.chartClassName}
              />
            )
          })}
          {label && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="fill-system-text-primary font-montserrat font-bold"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {label}
                    </text>
                  )
                }
              }}
            />
          )}
        </Pie>
      </PieChartRecharts>
    </ChartContainer>
  )
}

PieChart.displayName = 'PieChart'

export { PieChart, type PieChartProps }
